import { Directive, ElementRef, Input, OnDestroy, afterNextRender, booleanAttribute, inject, numberAttribute } from '@angular/core';
import { ScrollTrigger, gsap } from '../gsap';
import { QUERIES } from '../motion-env';
import { MotionService } from '../motion.service';
import { optionalNumber } from './inputs';

export type RevealPreset = '' | 'rise' | 'fade' | 'tip';

interface QueuedReveal {
  el: HTMLElement;
  play: (delay: number) => void;
  baseDelay: number;
}

// Elements that enter in the same frame play together, staggered in DOM order.
let queue: QueuedReveal[] = [];
let flushScheduled = false;

function enqueue(item: QueuedReveal): void {
  queue.push(item);
  if (flushScheduled) return;
  flushScheduled = true;
  gsap.delayedCall(0.05, () => {
    flushScheduled = false;
    const batch = queue.filter((q) => q.el.isConnected);
    queue = [];
    batch.sort((a, b) => (a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    const step = Math.min(0.08, 0.56 / Math.max(batch.length, 1));
    batch.forEach((q, i) => q.play(q.baseDelay + i * step));
  });
}

/**
 * Scroll-triggered entrance. `appReveal="rise|fade|tip"` (empty = rise). The hidden from-state
 * is applied by GSAP before paint, never by CSS, so content is visible without JS and under
 * reduced motion. Animates only opacity and transform (never autoAlpha), so the element stays
 * focusable and clickable.
 */
@Directive({ selector: '[appReveal]' })
export class RevealDirective implements OnDestroy {
  @Input('appReveal') preset: RevealPreset = '';
  @Input({ transform: numberAttribute }) revealDelay = 0;
  /** px; default rise 24, tip 40 (x0.6 on mobile). */
  @Input({ transform: optionalNumber }) revealDistance: number | null = null;
  /** s; default rise .7, fade .6, tip .8. */
  @Input({ transform: optionalNumber }) revealDuration: number | null = null;
  @Input() revealStart = 'top 85%';
  @Input({ transform: booleanAttribute }) revealBatch = false;

  private readonly el: HTMLElement = inject(ElementRef).nativeElement;
  private readonly motion = inject(MotionService);
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private played = false;
  private destroyed = false;

  constructor() {
    this.el.classList.add('dv-reveal');
    afterNextRender(() => this.motion.whenNeeded(this.el, () => this.destroyed || this.setup()));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mm?.revert();
  }

  private setup(): void {
    const el = this.el;
    const mm = (this.mm = gsap.matchMedia(el));
    // `reduce` is listed so the callback also runs (and settles the final state) when neither
    // motion nor mobile matches: gsap.matchMedia skips the callback if no condition matches.
    mm.add({ motion: QUERIES.motion, reduce: QUERIES.reduce, mobile: QUERIES.mobile }, (ctx, contextSafe) => {
      const conditions = ctx.conditions!;
      const finish = () => {
        el.classList.remove('dv-reveal--armed');
        el.classList.add('is-in');
      };
      // A media change (resize across the mobile breakpoint, rotation) re-runs this callback:
      // never hide something the user has already seen.
      if (!conditions['motion'] || this.played) {
        finish();
        return;
      }

      const rect = el.getBoundingClientRect();
      if (rect.bottom <= 0 || (this.motion.recentLanguageSwitch() && rect.top < window.innerHeight)) {
        finish();
        return;
      }

      const preset = this.preset || 'rise';
      const mobile = !!conditions['mobile'];
      const distance = this.revealDistance ?? (preset === 'tip' ? 40 : 24);
      const d = mobile ? Math.round(distance * 0.6) : distance;
      const duration = this.revealDuration ?? (preset === 'fade' ? 0.6 : preset === 'tip' ? 0.8 : 0.7);

      const from: gsap.TweenVars =
        preset === 'fade'
          ? { opacity: 0 }
          : preset === 'tip'
            ? { opacity: 0, y: d, rotationX: 10, transformPerspective: 1000, transformOrigin: '50% 100%' }
            : { opacity: 0, y: d };
      gsap.set(el, from);
      el.classList.remove('is-in');
      el.classList.add('dv-reveal--armed');

      // `fade` never touches transform, so it is safe on iframe and map wrappers.
      const to: gsap.TweenVars = preset === 'fade' ? { opacity: 1 } : { opacity: 1, y: 0, rotationX: 0 };
      const play = contextSafe!((delay: number) => {
        this.played = true;
        el.classList.remove('dv-reveal--armed');
        el.classList.add('is-in');
        gsap.to(el, {
          ...to,
          duration,
          ease: preset === 'fade' ? 'power1.out' : 'power3.out',
          delay,
          overwrite: 'auto',
          clearProps: preset === 'fade' ? 'opacity' : 'opacity,transform',
        });
      }) as (delay: number) => void;

      ScrollTrigger.create({
        trigger: el,
        start: this.revealStart,
        once: true,
        onEnter: () => (this.revealBatch ? enqueue({ el, play, baseDelay: this.revealDelay }) : play(this.revealDelay)),
      });

      // Never leave content hidden after a revert (destroy or a media-query flip).
      return () => {
        gsap.set(el, { clearProps: 'opacity,transform' });
        finish();
      };
    });
  }
}
